import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { 
  XMarkIcon,
  ArrowsRightLeftIcon,
  PlusIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  BuildingStorefrontIcon
} from '@heroicons/react/24/outline';
import { useAppData } from '../../contexts/AppDataContext';
import stockService from '../../services/stockService';

const TransferForm = ({ onClose }) => {
  const { 
    getProducts, 
    getWarehouses,
    addStockMovement,
    updateProductStock,
    purchases,
    sales
  } = useAppData();
  
  const [form, setForm] = useState({
    sourceWarehouseId: '',
    destinationWarehouseId: '',
    date: new Date().toISOString().split('T')[0],
    items: [],
    notes: ''
  });
  
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Charger les données
  useEffect(() => {
    setWarehouses(getWarehouses());
    setProducts(getProducts());
  }, [getWarehouses, getProducts]);
  
  // Fonction pour déterminer si un produit appartient à un entrepôt spécifique
  const isProductInWarehouse = (product, warehouseId) => {
    // Always convert IDs to strings for consistent comparison
    const productIdStr = String(product.id);
    const warehouseIdStr = String(warehouseId);
    
    // Use StockService to check if product is in warehouse
    return stockService.isProductInWarehouse(productIdStr, warehouseIdStr);
  };

  // Récupérer tous les produits disponibles dans l'entrepôt source
  const sourceWarehouseProducts = form.sourceWarehouseId 
    ? products.filter(product => {
        const isInWarehouse = isProductInWarehouse(product, form.sourceWarehouseId);
        console.log(`Product ${product.id} (${product.name}) in source warehouse ${form.sourceWarehouseId}? ${isInWarehouse}`);
        return isInWarehouse;
      })
    : [];
  
  // Log the number of products found in the source warehouse
  console.log(`Found ${sourceWarehouseProducts.length} products in source warehouse ${form.sourceWarehouseId}`);
  
  // Debug function to analyze all products
  const debugWarehouseProducts = () => {
    if (!form.sourceWarehouseId) return;
    
    console.group('Warehouse Products Debug');
    console.log(`Analyzing all products for warehouse ${form.sourceWarehouseId}`);
    console.log(`Total products in system: ${products.length}`);
    
    // Check each product
    products.forEach(product => {
      console.log(`\nProduct: ${product.id} - ${product.name}`);
      
      // Check warehouseIds
      if (product.warehouseIds && Array.isArray(product.warehouseIds)) {
        console.log(`warehouseIds: ${JSON.stringify(product.warehouseIds)}`);
      } else {
        console.log('No warehouseIds array');
      }
      
      // Check warehouseStock
      if (product.warehouseStock) {
        console.log(`warehouseStock: ${JSON.stringify(product.warehouseStock)}`);
      } else {
        console.log('No warehouseStock object');
      }
      
      // Check if in this warehouse
      const isInWarehouse = isProductInWarehouse(product, form.sourceWarehouseId);
      console.log(`Is in warehouse ${form.sourceWarehouseId}? ${isInWarehouse}`);
    });
    
    console.groupEnd();
  };
  
  // Run debug when source warehouse changes
  useEffect(() => {
    if (form.sourceWarehouseId) {
      debugWarehouseProducts();
    }
  }, [form.sourceWarehouseId, products, isProductInWarehouse]);
  
  // Filtrer les produits selon la recherche ou montrer tous les produits de l'entrepôt
  const filteredProducts = searchQuery.trim() === '' 
    ? (form.sourceWarehouseId ? sourceWarehouseProducts.slice(0, 50) : []) // Montrer les 50 premiers produits de l'entrepôt si aucune recherche
    : sourceWarehouseProducts.filter(product => 
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (product.sku && product.sku.toLowerCase().includes(searchQuery.toLowerCase()))
      );
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => {
      // Si on change l'entrepôt source, on vide la liste des produits
      if (name === 'sourceWarehouseId' && prev.sourceWarehouseId !== value) {
        return { ...prev, [name]: value, items: [] };
      }
      return { ...prev, [name]: value };
    });
  };
  
  const handleAddItem = () => {
    // Vérifier qu'un entrepôt source est sélectionné
    if (!form.sourceWarehouseId) {
      toast.error('Veuillez sélectionner un entrepôt source');
      return;
    }
    setShowProductSearch(true);
  };
  
  const handleSelectProduct = (product) => {
    // Convert warehouse ID to string for consistent comparison
    const sourceWarehouseIdStr = String(form.sourceWarehouseId);
    const productIdStr = String(product.id);
    
    // Use StockService to check if product is in warehouse and get its stock
    const isInWarehouse = stockService.isProductInWarehouse(productIdStr, sourceWarehouseIdStr);
    const productAvailableStock = stockService.getProductStockByWarehouse(productIdStr, sourceWarehouseIdStr);
    
    console.log(`Product ${product.name} has ${productAvailableStock} units available in warehouse ${sourceWarehouseIdStr}`);
    
    // If no stock found, check if the product is actually in this warehouse
    if (productAvailableStock <= 0) {
      if (!isInWarehouse) {
        toast.error(`Ce produit n'est pas disponible dans cet entrepôt`);
        return;
      } else if (productAvailableStock <= 0) {
        toast.error(`Ce produit n'a pas de stock disponible dans cet entrepôt`);
      return;
      }
    }
    
    // Vérifier que le produit n'est pas déjà dans la liste
    if (form.items.some(item => item.id === product.id)) {
      toast.error('Ce produit est déjà dans la liste');
      return;
    }
    
    // Get the available stock from StockService
    let itemAvailableStock = productAvailableStock;
    
    // If no stock found but product is in warehouse, set a minimum stock of 1
    if (itemAvailableStock <= 0 && isInWarehouse) {
      itemAvailableStock = 1;
    }
    
    const newItem = {
      id: product.id,
      name: product.name,
      sku: product.sku,
      stock: itemAvailableStock,
      quantity: 1
    };
    
    setForm(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
    
    setShowProductSearch(false);
    setSearchQuery('');
  };
  
  const handleRemoveItem = (index) => {
    setForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };
  
  const handleItemChange = (index, value) => {
    const updatedItems = [...form.items];
    
    // Limiter la quantité au stock disponible
    const maxQuantity = updatedItems[index].stock;
    updatedItems[index].quantity = Math.min(Math.max(1, Number(value)), maxQuantity);
    
    setForm(prev => ({
      ...prev,
      items: updatedItems
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!form.sourceWarehouseId) {
      toast.error('Veuillez sélectionner un entrepôt source');
      return;
    }
    
    if (!form.destinationWarehouseId) {
      toast.error('Veuillez sélectionner un entrepôt de destination');
      return;
    }
    
    if (form.sourceWarehouseId === form.destinationWarehouseId) {
      toast.error('Les entrepôts source et destination doivent être différents');
      return;
    }
    
    if (!form.items || !form.items.length) {
      toast.error('Veuillez ajouter au moins un produit');
      return;
    }
    
    // Vérifier les quantités
    const invalidQuantities = form.items.filter(item => !item.quantity || item.quantity <= 0);
    if (invalidQuantities.length) {
      toast.error('Quantités invalides');
      return;
    }
    
    try {
      setSaving(true);
      console.log('Starting transfer with StockService...');
      
      // Convert warehouse IDs to strings for consistent handling
      const sourceWarehouseIdStr = String(form.sourceWarehouseId);
      const destWarehouseIdStr = String(form.destinationWarehouseId);
      
      // Use the new StockService for transfers
      const transferResult = await stockService.transferStock({
        sourceWarehouseId: sourceWarehouseIdStr,
        destinationWarehouseId: destWarehouseIdStr,
        items: form.items.map(item => ({
          ...item,
          id: String(item.id) // Ensure product IDs are strings
        })),
        date: form.date,
        notes: form.notes
      });
      
      console.log('Transfer completed:', transferResult);
      
      // Check results
      const successCount = transferResult.processedItems.length;
      const errorCount = transferResult.failedItems.length;
      
      // Force immediate UI update by dispatching multiple events to ensure all listeners receive it
      
      // First dispatch a warehouse-inventory-updated event (custom event for our app)
      const updateEvent = new CustomEvent('warehouse-inventory-updated', {
        detail: {
          sourceWarehouseId: sourceWarehouseIdStr,
          destinationWarehouseId: destWarehouseIdStr,
          items: transferResult.processedItems,
          timestamp: new Date().toISOString()
        }
      });
      window.dispatchEvent(updateEvent);
      
      // Then dispatch transfer-completed event (this is what stockService uses)
      const transferEvent = new CustomEvent('transfer-completed', {
        detail: {
          sourceWarehouseId: sourceWarehouseIdStr,
          destinationWarehouseId: destWarehouseIdStr,
          items: transferResult.processedItems,
          timestamp: new Date().toISOString()
        }
      });
      window.dispatchEvent(transferEvent);
      
      // Also dispatch a general refresh-data event as a fallback
      const refreshEvent = new CustomEvent('refresh-data');
      window.dispatchEvent(refreshEvent);
      
      // Force a direct refresh of localStorage data to ensure fresh data
      try {
        // Get fresh data directly from localStorage
        const freshProducts = JSON.parse(localStorage.getItem('stockpro_products') || '[]');
        
        // Force update the localStorage again to ensure it's consistent
        localStorage.setItem('products', JSON.stringify(freshProducts));
        localStorage.setItem('stockpro_products', JSON.stringify(freshProducts));
        
        // Also update all movements
        const freshMovements = JSON.parse(localStorage.getItem('stockpro_stock') || '[]');
        localStorage.setItem('stockpro_stock', JSON.stringify(freshMovements));
        
        console.log('Force-refreshed localStorage data to ensure consistency');
      } catch (e) {
        console.error('Error updating localStorage directly:', e);
      }
      
      // Display appropriate notifications
      if (successCount === form.items.length) {
        toast.success('Transfert effectué avec succès');
        
        // Force direct warehouse page update before closing
        setTimeout(() => {
          try {
            // Force immediate reload of both source and destination warehouses' data
            console.log('TRANSFER SUCCESS: Force-reloading both warehouses data');
            
            // Directly force update localStorage for consistency
            const products = JSON.parse(localStorage.getItem('stockpro_products') || '[]');
            localStorage.setItem('products', JSON.stringify(products)); // Ensure both keys are in sync
            
            // Force global refresh events
            window.dispatchEvent(new CustomEvent('refresh-data'));
            window.dispatchEvent(new CustomEvent('warehouse-inventory-updated', {
              detail: { force: true, warehouseId: sourceWarehouseIdStr }
            }));
            window.dispatchEvent(new CustomEvent('warehouse-inventory-updated', {
              detail: { force: true, warehouseId: destWarehouseIdStr }
            }));
            
            // If we're on a warehouse page, reload it immediately
            if (window.location.href.includes('/warehouses/')) {
              console.log('On warehouse page, forcing reload in 500ms...');
              setTimeout(() => {
                // Use app global refresh if available
                if (typeof window.refreshAppData === 'function') {
                  window.refreshAppData();
                }
                // If on a specific warehouse page that needs refreshing
                if (window.location.href.includes('/inventory/')) {
                  console.log('On inventory page, reloading browser...');
                  // Sometimes we need a full reload to see updates
                  window.location.reload();
                }
              }, 500);
            }
          } catch (e) {
            console.error('Error during final refresh:', e);
          }
          
          // Finally close the form
          onClose();
        }, 500);
      } else if (successCount > 0) {
        toast.success(`${successCount} produits transférés sur ${form.items.length}`);
        toast.error(`${errorCount} produits n'ont pas pu être transférés`);
        
        // Force direct warehouse page update before closing
        setTimeout(() => {
          try {
            // Force immediate reload of both source and destination warehouses' data
            console.log('TRANSFER SUCCESS: Force-reloading both warehouses data');
            
            // Directly force update localStorage for consistency
            const products = JSON.parse(localStorage.getItem('stockpro_products') || '[]');
            localStorage.setItem('products', JSON.stringify(products)); // Ensure both keys are in sync
            
            // Force global refresh events
            window.dispatchEvent(new CustomEvent('refresh-data'));
            window.dispatchEvent(new CustomEvent('warehouse-inventory-updated', {
              detail: { force: true, warehouseId: sourceWarehouseIdStr }
            }));
            window.dispatchEvent(new CustomEvent('warehouse-inventory-updated', {
              detail: { force: true, warehouseId: destWarehouseIdStr }
            }));
            
            // If we're on a warehouse page, reload it immediately
            if (window.location.href.includes('/warehouses/')) {
              console.log('On warehouse page, forcing reload in 500ms...');
              setTimeout(() => {
                // Use app global refresh if available
                if (typeof window.refreshAppData === 'function') {
                  window.refreshAppData();
                }
                // If on a specific warehouse page that needs refreshing
                if (window.location.href.includes('/inventory/')) {
                  console.log('On inventory page, reloading browser...');
                  // Sometimes we need a full reload to see updates
                  window.location.reload();
                }
              }, 500);
            }
          } catch (e) {
            console.error('Error during final refresh:', e);
          }
          
          // Finally close the form
          onClose();
        }, 500);
      } else {
        toast.error('Erreur: Aucun produit n\'a pu être transféré. Vérifiez la console pour plus de détails.');
      }
    } catch (error) {
      console.error('Erreur lors du transfert:', error);
      toast.error(`Erreur lors du transfert: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Récupérer les noms des entrepôts
  const getWarehouseName = (id) => {
    const warehouse = warehouses.find(w => w.id === id);
    return warehouse ? warehouse.name : 'Inconnu';
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose}></div>
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 30, opacity: 0 }}
        className="bg-gray-900/95 border border-white/10 rounded-3xl w-full max-w-4xl z-10 overflow-hidden max-h-[90vh] flex flex-col"
      >
        <div className="p-6 border-b border-white/10 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-white flex items-center">
            <ArrowsRightLeftIcon className="h-7 w-7 mr-3 text-blue-400" />
            Transfert entre entrepôts
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-full text-white/60 hover:text-white"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto flex-grow">
          {/* Entrepôts source et destination */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <h3 className="text-xl font-semibold text-white mb-4">Informations du transfert</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="sourceWarehouseId" className="block text-white/80 text-sm mb-1">
                  Entrepôt source <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <BuildingStorefrontIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                  <select
                    id="sourceWarehouseId"
                    name="sourceWarehouseId"
                    value={form.sourceWarehouseId}
                    onChange={handleChange}
                    className="w-full pl-10 p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">-- Sélectionner un entrepôt --</option>
                    {warehouses.map(warehouse => (
                      <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label htmlFor="destinationWarehouseId" className="block text-white/80 text-sm mb-1">
                  Entrepôt destination <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <BuildingStorefrontIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                  <select
                    id="destinationWarehouseId"
                    name="destinationWarehouseId"
                    value={form.destinationWarehouseId}
                    onChange={handleChange}
                    className="w-full pl-10 p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">-- Sélectionner un entrepôt --</option>
                    {warehouses.map(warehouse => (
                      <option 
                        key={warehouse.id} 
                        value={warehouse.id}
                        disabled={warehouse.id === form.sourceWarehouseId}
                      >
                        {warehouse.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            
            <div className="mt-6">
              <label htmlFor="date" className="block text-white/80 text-sm mb-1">
                Date du transfert
              </label>
              <input
                type="date"
                id="date"
                name="date"
                value={form.date}
                onChange={handleChange}
                className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          {/* Produits */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-white">Articles à transférer</h3>
              
              <button
                type="button"
                onClick={handleAddItem}
                disabled={!form.sourceWarehouseId}
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors py-2 px-4 rounded-xl text-white flex items-center"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                <span>Ajouter un produit</span>
              </button>
            </div>
            
            {showProductSearch && (
              <div className="mb-6 bg-white/5 rounded-xl p-4 border border-white/10">
                <div className="flex items-center mb-4">
                  <h4 className="text-white font-medium">
                    Rechercher un produit dans {getWarehouseName(form.sourceWarehouseId)}
                  </h4>
                  <button
                    type="button"
                    onClick={() => setShowProductSearch(false)}
                    className="ml-auto p-1.5 bg-white/10 rounded-lg hover:bg-white/20 text-white"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                
                <div className="relative mb-4">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Rechercher par nom ou référence"
                    className="w-full pl-10 p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div className="max-h-64 overflow-y-auto bg-white/5 rounded-lg">
                  <table className="min-w-full divide-y divide-white/10">
                    <thead className="bg-white/5">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-white/70">Référence</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-white/70">Produit</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-white/70">Stock</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-white/70">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.length > 0 ? (
                        filteredProducts.map((product) => (
                          <tr key={product.id} className="hover:bg-white/10">
                            <td className="px-4 py-2 text-sm text-white/80">{product.sku || '-'}</td>
                            <td className="px-4 py-2 text-sm text-white">{product.name}</td>
                            <td className="px-4 py-2 text-sm text-white text-right">{product.stock}</td>
                            <td className="px-4 py-2 text-center">
                              <button
                                type="button"
                                onClick={() => handleSelectProduct(product)}
                                className="p-1.5 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg text-blue-300"
                              >
                                <PlusIcon className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : searchQuery.trim() !== '' ? (
                        <tr>
                          <td colSpan="4" className="px-4 py-4 text-center text-white/60">
                            Aucun produit trouvé dans cet entrepôt
                          </td>
                        </tr>
                      ) : sourceWarehouseProducts.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="px-4 py-4 text-center text-white/60">
                            Aucun produit n'est disponible dans cet entrepôt
                          </td>
                        </tr>
                      ) : (
                        <tr>
                          <td colSpan="4" className="px-4 py-4 text-center text-white/60">
                            Chargement des produits...
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {form.items.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-white/10">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white/70">Produit</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white/70">Référence</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-white/70">Quantité</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-white/70">Stock disponible</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-white/70">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {form.items.map((item, index) => (
                      <tr key={index}>
                        <td className="px-4 py-3 text-sm text-white">{item.name}</td>
                        <td className="px-4 py-3 text-sm text-white/80">{item.sku || '-'}</td>
                        <td className="px-4 py-3 text-sm">
                          <input
                            type="number"
                            min="1"
                            max={item.stock}
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, e.target.value)}
                            className="w-20 p-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm text-white text-right">{item.stock}</td>
                        <td className="px-4 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(index)}
                            className="p-1.5 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-300 transition-colors"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 border border-dashed border-white/20 rounded-xl">
                <ArrowsRightLeftIcon className="h-12 w-12 mx-auto text-white/30 mb-2" />
                <p className="text-white/60">Aucun produit ajouté à ce transfert</p>
                {!form.sourceWarehouseId ? (
                  <p className="text-white/50 text-sm mt-1">
                    Sélectionnez d'abord un entrepôt source
                  </p>
                ) : (
                  <p className="text-white/50 text-sm mt-1">
                    Cliquez sur "Ajouter un produit" pour commencer
                  </p>
                )}
              </div>
            )}
          </div>
          
          {/* Notes */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <label htmlFor="notes" className="block text-white font-medium mb-2">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={form.notes}
              onChange={handleChange}
              rows="3"
              placeholder="Notes additionnelles sur ce transfert..."
              className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          {/* Boutons d'action */}
          <div className="flex justify-end space-x-4 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white rounded-xl font-semibold transition-colors flex items-center"
            >
              {saving ? (
                <>
                  <span className="animate-spin h-4 w-4 mr-2 border-b-2 border-white rounded-full"></span>
                  Traitement...
                </>
              ) : 'Effectuer le transfert'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default TransferForm;